import { button, countOf, eventTag, line, type MenuTab } from "./shared";
import { type JournalEntry, provenanceOf } from "../../journal/journal";
import { type RememberedPerson, meetingOf } from "../../journal/people";
import { type EventId, MAX_CHRONICLE_ENTRIES } from "../../world/types";

/*
 * The journal, and the page about the player: two of the menu's four
 * (chunk U-0's split of `panels.ts`; chunk U-3's move into the menu).
 *
 * The share section and the compile mode are sections of this panel rather than
 * panels of their own, and that is the design rather than an economy: what the
 * player is handing over and what they are writing down is exactly what the
 * journal already holds. The five render helpers below are this panel's alone —
 * they are here rather than in `shared.ts` because nothing else draws a person,
 * an entry, a book bar, an offer or a heading with a citation on it.
 *
 * # Why one class fills two pages
 *
 * The **you** page is the journal's own view read a second way. Everything on
 * it — the sentence the world says about the player, how much they have
 * learned, how many people they have met — arrives inside {@link JournalView},
 * which `witness/witness.ts` fills in one call. So the page that says who the
 * player is has no reader of its own and no boundary read of its own; it has
 * this one, and putting it in a second class would mean a second copy of the
 * view crossing to reach it.
 *
 * That is also the lab's own reasoning for moving it (`menu-tabs.html`): the
 * journal was carrying the introduction line in its lead **only because there
 * was nowhere else to put it** (Phase 5 decision 6c). There is somewhere else
 * now, and the journal is a ledger again.
 */

/**
 * What the you page says before a claim has landed.
 *
 * One string in one place, because two pages of this file write it: the
 * constructor, so the page is never blank for a session that has not opened the
 * journal yet, and every redraw where the world still names nobody.
 */
const NOT_NAMED = "the record does not name you yet";

/**
 * The journal: everything the player learned, how they learned it, and who they
 * learned it from — and the page about the figure whose journal it is.
 *
 * A list of references, displayed with their provenance — never a list of
 * stories (plan decision 10). Entries are shown oldest first, which is the order
 * they were learned in and therefore the order they read in.
 *
 * **The people are a column of their own since chunk U-3.** Phase 4 decision 6
 * asks for an anchor — a named figure met in the first minutes whose fate rides
 * the simulation — and the client's honest half of it is memory: who the player
 * actually stood in front of, where they were from, and what has happened since
 * that touches them (`journal/people.ts`). They were at the *top* of the old
 * panel because a column of one was the only place to put them; on a page twice
 * as wide they sit beside the records instead, which is the arrangement the lab
 * drew and the one the two lists deserve — the records are what the player
 * knows and the people are who they know it from.
 *
 * **The register**: serif is what the world claims — a note, a tie, a name — and
 * sans is how the player came by it — a provenance, a meeting, a count. One
 * typeface each tells a page of claims from its margin of bookkeeping without
 * drawing a rule (chunk U-1's rule for the HUD, applied to a page).
 */
export class JournalOverlay {
  private readonly tab: MenuTab;
  private readonly youTab: MenuTab;

  private readonly bodyElement: HTMLElement;
  private readonly whoElement: HTMLElement;
  private readonly knownElement: HTMLElement;
  private readonly shareElement: HTMLElement;
  private readonly bookElement: HTMLElement;
  private readonly peopleElement: HTMLElement;
  private readonly countElement: HTMLElement;

  /** The scrolling left column, so choosing pages can be scrolled to. */
  private readonly pageElement: HTMLElement;

  /** Whether a book was on the table last time this was drawn. See {@link JournalOverlay.show}. */
  private compiling = false;

  /**
   * `tab` is the menu's journal page and `youTab` its first — see the note at
   * the top of this file for why one class fills both.
   *
   * `touch` used to reach the line at the foot saying which key shuts the panel,
   * and does not any more: the menu says that once for all four pages
   * (`hud/panels/menu.ts`). Neither of these pages has a key of its own to
   * mention, so neither sets a note.
   */
  constructor(tab: MenuTab, youTab: MenuTab) {
    this.tab = tab;
    this.youTab = youTab;

    // ---- the you page ------------------------------------------------------
    //
    // The introduction and the two counts, left at the content edge and
    // deliberately not filling the width. There is nothing else true about this
    // life to put in the empty half, and the honest way to show that is to leave
    // it empty rather than to pad it out with a portrait and six numbers
    // (`ui-lab/surfaces/menu-tabs.html`).

    // The player, if they have claimed a name. Its old name kept: it was the
    // journal's lead (Phase 5 decision 6c) and the element is the same element,
    // moved to the page the lab gave it.
    this.whoElement = document.createElement("div");
    this.whoElement.className = "hud__panel-who";
    // Filled at construction rather than left blank, because this page can be
    // reached before anything has ever filled it — a session that presses `Tab`
    // before it has opened the journal, and every `new Hud(root)` in a check.
    // The empty state is the honest one either way (see {@link JournalOverlay.show}).
    this.whoElement.append(line(NOT_NAMED));

    // "12 things learned · 2 people met" — the journal's own two tallies, and
    // the count element the journal drew one of them in. Bookkeeping, so sans.
    this.countElement = document.createElement("div");
    this.countElement.className = "hud__panel-count";

    const youColumn = document.createElement("div");
    youColumn.className = "hud__you-column";
    youColumn.append(this.whoElement, this.countElement);
    youTab.body.append(youColumn);

    // **Known for** — the byname a figure's own records earned them
    // (`sim_core::history::epithet`, read as {@link WitnessSource.byname}),
    // honestly empty for most people forever. Chunk U-3 refused to draw it
    // because no member carried it; the read landed beside wave U2 and the
    // element is the four lines the refusal promised. The byname is the sim's
    // word, verbatim, never assembled with the name (decision 6b).
    this.knownElement = document.createElement("div");
    this.knownElement.className = "hud__you-known known-tags";
    this.knownElement.hidden = true;
    youColumn.append(this.knownElement);

    // ---- the journal page --------------------------------------------------

    // What this town has not heard, and the button that hands one over.
    this.shareElement = document.createElement("div");
    this.shareElement.className = "hud__panel-share";
    this.shareElement.hidden = true;

    this.bodyElement = document.createElement("div");
    this.bodyElement.className = "hud__panel-body";

    // The two lists, side by side: the records the player holds on the left and
    // the people they hold them from on the right. The kit's `.pane` draws the
    // hairline BETWEEN them rather than round them, and each column scrolls on
    // its own — which is what a page twice as wide buys over the one scrolling
    // middle the old panel had (`ui/panel.css`'s `.hud__panel-scroll`).
    this.pageElement = document.createElement("div");
    // `hud__panel-scroll` kept on the scroller: it is the name `ui/base.css`
    // gives `touch-action: pan-y` to, and a column a finger cannot drag is a
    // column a phone cannot read.
    this.pageElement.className = "hud__panel-scroll pane-body";
    this.pageElement.append(this.shareElement, this.bodyElement);

    this.peopleElement = document.createElement("div");
    this.peopleElement.className = "hud__panel-people";
    this.peopleElement.hidden = true;

    const peopleScroll = document.createElement("div");
    peopleScroll.className = "hud__panel-scroll pane-body";
    peopleScroll.append(this.peopleElement);

    const records = document.createElement("div");
    records.className = "pane";
    records.append(this.pageElement);

    const met = document.createElement("div");
    met.className = "pane";
    met.append(peopleScroll);

    const grid = document.createElement("div");
    grid.className = "hud__journal-grid";
    grid.append(records, met);

    // The book on the table, and the two buttons that start and finish one.
    //
    // **On the journal's foot** (chunk U-3, closing the lab's open question):
    // compiling a chronicle is an act *over the journal's records* — the lines
    // it chooses are the lines above it and nothing else on the screen is
    // involved — so this is its home, and it is at the foot rather than at the
    // head because the page it acts on is what a reader should meet first.
    // It keeps `hud__panel-share`'s class for the `[hidden]` rule it shares with
    // the offer above (`ui/journal.css`).
    this.bookElement = document.createElement("div");
    this.bookElement.className = "hud__panel-share hud__journal-foot";

    tab.body.append(grid, this.bookElement);
  }

  get open(): boolean {
    return this.tab.showing;
  }

  /**
   * Rebuilds every list and shows the journal page.
   *
   * Everything except the entries is optional, and the defaults are what a
   * caller with nothing to add gets — the self-checks, and any session with no
   * claim, no town within reach and no book being compiled. That is the same
   * arrangement `people` has had since Phase 4 and it is what keeps this panel
   * usable from a check that owns no simulation.
   *
   * **Every string handed in comes from somewhere else**, and the split is the
   * house rule rather than a convenience: {@link JournalView.figure} is
   * `speech::introduce`'s own sentence about the player's figure, the share
   * rows are the player's own journal notes, and the callbacks queue commands
   * the caller owns. This file writes headings and buttons and nothing about
   * the world.
   *
   * **It fills the you page too** and does not reveal it: which page ends up on
   * screen is the caller's, and the caller here is the `J` key
   * (`witness/witness.ts`'s `toggleJournal`). The menu's own door asks for this
   * fill and then selects *you* over the top of it, which is how a page with no
   * reader of its own is reachable at all (`hud/panels/menu.ts`).
   */
  show(view: JournalView): void {
    const { entries, people = [], figure = null, byname = "", share = null, book = null } = view;
    // Where the reader had got to. Every section below is rebuilt whole, and a
    // container whose content is emptied for an instant has its scroll position
    // clamped to nothing on the way past — so a journal left open while the day
    // turned, or while a page was being chosen, would jump back to the top under
    // somebody's hands. Restored at the end, unless a book has just gone on the
    // table and wants the page moved deliberately.
    const scrolled = this.pageElement.scrollTop;

    // **The two tallies on one line, on the you page.** Both were already on the
    // old panel — one in this element, one inside the people heading — and both
    // are the client counting its own store rather than asking the world
    // anything, which is why they may be joined.
    this.countElement.textContent =
      entries.length === 0 && people.length === 0
        ? ""
        : `${countOf(entries.length, "thing")} learned · ${people.length} ${
            people.length === 1 ? "person" : "people"
          } met`;

    // **A book on the table is a mode, and the column clears for it.** The offer
    // above the entries is what a player is *not* doing while they are choosing
    // pages, and it stands between them and the lines they are pressing. The
    // people no longer go with it: they are a column of their own since U-3 and
    // hiding them buys the entries no room at all.
    const picking = book?.compiling === true;

    this.whoElement.replaceChildren(
      figure === null
        ? // The honest empty state, and it is a real one: `playerFigure()` is
          // `null` in every session before the claim lands and in every world
          // that has no claim door at all (`world/fixture.ts`). A dim serif line
          // rather than a dashed placeholder box — the record does not name this
          // player yet, which is a fact about the world and not a page waiting
          // to be filled.
          line(NOT_NAMED)
        : youLine(figure),
    );
    // Hidden rather than emptied when there is nothing: a heading over an
    // empty list would be the page promising something the records never
    // wrote. Shown, it is one tag, the byname whole.
    this.knownElement.hidden = figure === null || byname === "";
    this.knownElement.replaceChildren(...(byname === "" ? [] : [knownTag(byname)]));

    this.shareElement.hidden = picking || share === null || share.entries.length === 0;
    this.shareElement.replaceChildren(
      ...(share === null || share.entries.length === 0
        ? []
        : [
            heading(`${share.where} has not heard ${countOf(share.entries.length, "thing")}`),
            ...share.entries.map((entry) => renderShare(entry, share)),
          ]),
    );

    this.peopleElement.hidden = people.length === 0;
    this.peopleElement.replaceChildren(
      ...(people.length === 0
        ? []
        : [
            heading(`people you have met (${people.length})`),
            ...people.map((person) => renderPerson(person)),
          ]),
    );

    this.bookElement.hidden = book === null || entries.length === 0;
    this.bookElement.replaceChildren(
      ...(book === null || entries.length === 0 ? [] : [renderBookBar(book)]),
    );

    this.bodyElement.replaceChildren(
      ...(entries.length === 0
        ? [line("nothing learned yet — talk to somebody, read the board, look at the ground")]
        : [heading("what you learned"), ...entries.map((entry) => renderEntry(entry, book))]),
    );
    this.tab.reveal();

    // A book going on the table scrolls the column to the lines being chosen,
    // once and only on the change: the entries are the last section in it and a
    // compile mode whose lines are below the bottom of the column is a verb
    // nobody finds. Doing it on every redraw instead would take the page out of
    // a reader's hands every time the journal refreshed under them, which is the
    // mistake `map/panel.ts` names about re-fitting the paper.
    this.pageElement.scrollTop = picking === this.compiling ? scrolled : 0;
    this.compiling = picking;
  }

  hide(): void {
    this.tab.close();
  }

  /**
   * Whether the *you* page is the one on screen.
   *
   * For the self-checks, and it is the one thing about that page nothing else
   * can ask: it has no panel class of its own to call `show` on, because what
   * fills it is this one.
   */
  get youOpen(): boolean {
    return this.youTab.showing;
  }
}

/**
 * The share-word offer, as the journal draws it.
 *
 * The entries are the caller's comparison of two lists the simulation already
 * answers with — what the journal holds, minus what `rumors(here)` says this
 * town already has (`world/types.ts`, {@link WorldSource.tell}). Nothing here
 * decides which of them is worth telling: the player does, by pressing a
 * button, and the simulation decides whether it was news.
 */
export interface JournalShare {
  /** The town, as the simulation spells it. Never composed here. */
  readonly where: string;

  /** What the player holds that this town does not, oldest first. */
  readonly entries: readonly JournalEntry[];

  /** Hands one over: `WorldSource.tell(here, entry.event)`. */
  readonly onTell: (entry: JournalEntry) => void;
}

/**
 * The book being compiled, as the journal draws it.
 *
 * Phase 5 decision 5's client half: a chronicle is **an ordered list of record
 * ids and not one word of prose**, so the whole of this UI is choosing which
 * records and in what order. The order is the order they were picked, which is
 * the only ordering a player can express by clicking and the only one this
 * panel is entitled to invent.
 *
 * Picked by *record* rather than by entry, because that is what a book cites: a
 * war heard from a carter and read off a board is one page, and both rows show
 * the same number.
 */
export interface JournalBook {
  /** Whether the player is choosing pages right now. */
  readonly compiling: boolean;

  /** The records chosen, in the order they will be cited. */
  readonly picked: readonly EventId[];

  /** Adds a record, or takes it out again. Bounded by the caller. */
  readonly onPick: (event: EventId) => void;

  /** Starts or stops choosing. */
  readonly onToggle: () => void;

  /** Leaves the book on the ground: `WorldSource.placeChronicle`. */
  readonly onPlace: () => void;
}

/** Everything the journal draws. See {@link JournalOverlay.show}. */
export interface JournalView {
  readonly entries: readonly JournalEntry[];
  readonly people?: readonly RememberedPerson[];

  /**
   * The simulation's own introduction of the player's figure, or `null` before
   * a name has been claimed.
   *
   * `speech::introduce` over the player's own record — their name, where they
   * are of, and one facet-coloured clause (`world/types.ts`,
   * {@link WitnessSource.introduce}). It is the whole of what this client can
   * read about who the player is, and it is the right shape: a client that
   * assembled an identity out of two strings would be composing one.
   */
  readonly figure?: string | null;

  /**
   * The byname the player's figure earned by their own records, or `""` —
   * which is most figures, forever ({@link WitnessSource.byname}). Drawn under
   * the introduction as one tag and never joined to it.
   */
  readonly byname?: string;

  readonly share?: JournalShare | null;
  readonly book?: JournalBook | null;
}

/**
 * The one sentence the world says about the player, at the top of their own
 * page.
 *
 * Serif and large, because it is the world talking and it is the only thing on
 * the page that is (chunk U-1's register, applied to a page). Never composed
 * here and never split: `introduce` writes the whole line.
 */
function youLine(figure: string): HTMLElement {
  const element = document.createElement("p");
  element.className = "hud__you-line";
  element.textContent = figure;

  return element;
}

/** One tag under the introduction: *known for* in the interface's hand, the byname in the world's. */
function knownTag(byname: string): HTMLElement {
  const label = document.createElement("span");
  label.className = "eyebrow";
  label.textContent = "known for";
  const tag = document.createElement("span");
  tag.className = "hud__you-byname";
  tag.textContent = byname;
  const element = document.createElement("div");
  element.className = "hud__you-known-row";
  element.append(label, tag);

  return element;
}

/**
 * A section heading, in the kit's own register.
 *
 * `.sim-heading` — serif, a gold diamond and a hairline to the edge — under a
 * name of this screen's own (plan decision D2), and **never `.section-label`**,
 * which uppercases: every heading on this page carries a name or a number the
 * client composed around one (*"Eiroddcrag has not heard 3 things"*), and
 * `text-transform` on anything that can hold a name is this client's oldest
 * recorded UI bug.
 *
 * **A new name rather than `shared.ts`'s `heading`**, which draws
 * `hud__panel-heading` — the trace view draws that one too, in the debug
 * register, and it must not move with the menu. Minting one is also what lets
 * the kit show through at all: `ui/kit.css` is imported before every client
 * sheet, so a `hud__` class that states a colour beats `.sim-heading` whatever
 * the markup says, and a name nobody has styled states nothing to beat it with.
 */
function heading(text: string): HTMLElement {
  const element = document.createElement("div");
  element.className = "hud__menu-heading sim-heading";
  element.textContent = text;

  return element;
}

/**
 * A record's id, as the chip every page of this menu cites one with.
 *
 * The kit's `.cite`, and it is a *child* of the provenance line rather than
 * text appended to it — which is a shape a driver already depends on: the book
 * smoke reads the id off the end of `.hud__journal-provenance`'s `textContent`,
 * and a child element is still inside that (`scripts/live-smoke.mjs`).
 */
function cite(event: EventId): HTMLElement {
  const element = document.createElement("span");
  element.className = "hud__journal-cite cite";
  element.textContent = eventTag(event);

  return element;
}

/**
 * One person the player met, and everything that ties them to what has
 * happened since.
 *
 * Every tie carries the records it rests on, drawn the way every other claim in
 * this HUD is: a dim gold `#id` the player can put through the trace view
 * (`journal/people.ts` decides what may be said; this decides what it looks
 * like). The ties hang off the name on a hairline rail rather than running into
 * it, which is the lab's own arrangement: a name is the thing a reader is
 * looking for and everything under it is about that name.
 */
function renderPerson(person: RememberedPerson): HTMLElement {
  const element = document.createElement("div");
  element.className = "hud__person";

  const name = document.createElement("div");
  name.className = "hud__person-name";
  name.textContent =
    person.met.homeName === ""
      ? person.met.name
      : `${person.met.name} — of ${person.met.homeName}`;

  const met = document.createElement("div");
  met.className = "hud__person-met";
  met.textContent = meetingOf(person.met);

  element.append(name, met);

  const ties = document.createElement("div");
  ties.className = "hud__person-ties";

  for (const tie of person.ties) {
    const drawn = document.createElement("div");
    drawn.className = "hud__person-tie";
    drawn.textContent = tie.text;
    drawn.append(...tie.refs.map((event) => cite(event)));
    ties.append(drawn);
  }

  if (person.ties.length > 0) {
    element.append(ties);
  }

  return element;
}

/**
 * One journal entry: what was heard, from whom and when, and which record it
 * was — and, while a book is being compiled, whether it is a page of it.
 *
 * The compile affordance is the *row itself* rather than a control beside it:
 * choosing pages is the only thing the page is for while it is happening, and
 * a column of checkboxes down a page of prose is a form. A picked row carries
 * its place in the book at the front of the line, which is the whole of the
 * ordering UI — click order is citation order (see {@link JournalBook}).
 */
function renderEntry(entry: JournalEntry, book: JournalBook | null): HTMLElement {
  const element = document.createElement("div");
  element.className = "hud__journal-entry";

  const mark = document.createElement("span");
  // The kit's bullet, hung in its own grid column rather than run into the
  // text, so a wrapped note keeps its left edge. Hollow on a record already on
  // file and filled on an offer, which is the lab's own distinction: one is a
  // thing to do and one is a thing that is done.
  mark.className = "objective-diamond hollow hud__journal-mark";
  mark.setAttribute("aria-hidden", "true");

  const note = document.createElement("div");
  note.className = "hud__journal-note";
  note.textContent = entry.note;

  const provenance = document.createElement("div");
  provenance.className = "hud__journal-provenance";
  // The event id is the debug hook, and it sits next to the provenance rather
  // than hidden behind a toggle: the two together are the whole claim — this is
  // where the player heard it, and this is the record it really was.
  provenance.textContent = provenanceOf(entry);
  provenance.append(cite(entry.event));

  element.append(mark, note, provenance);

  if (book === null || !book.compiling) {
    return element;
  }

  const place = book.picked.indexOf(entry.event);

  element.classList.add("hud__journal-entry--picking");

  if (place >= 0) {
    element.classList.add("hud__journal-entry--picked");
    note.textContent = `${place + 1}.  ${entry.note}`;
  }

  // Clickable, so `.hud`'s `pointer-events: none` has to be turned back on for
  // it — the rule in `ui/base.css`. The menu already has the mouse: opening it
  // hands the pointer lock back (`main.ts`).
  element.addEventListener("click", (event) => {
    event.stopPropagation();
    book.onPick(entry.event);
  });

  return element;
}

/**
 * The bar at the foot of the journal while a book is on the table: what it is
 * for, how many pages are chosen, and the two buttons.
 *
 * The cap is read from the boundary rather than written down
 * ({@link MAX_CHRONICLE_ENTRIES}), because it is the simulation's number and a
 * seventeenth citation is dropped by the type that carries them whatever this
 * panel says. Knowing it is what lets the UI stop offering pages instead of
 * letting one go missing.
 */
function renderBookBar(book: JournalBook): HTMLElement {
  const element = document.createElement("div");
  element.className = "hud__book-bar";

  const said = document.createElement("div");
  said.className = "hud__book-said";
  said.textContent = book.compiling
    ? `${book.picked.length} of ${MAX_CHRONICLE_ENTRIES} chosen — press a line to add it, press it again to take it out`
    : "you can write down what you know and leave it where somebody will find it";

  element.append(
    said,
    button(book.compiling ? "put the book away" : "write a book", () => {
      book.onToggle();
    }),
  );

  if (book.compiling) {
    element.append(
      button(
        "leave it here",
        () => {
          book.onPlace();
        },
        book.picked.length === 0,
      ),
    );
  }

  return element;
}

/**
 * One thing the player knows that this town does not, with the button that
 * hands it over.
 *
 * The note is the player's own record of what they were told, shown so they can
 * tell one from another — never a second sentence about the record (the note is
 * itself the simulation's line, kept verbatim: `journal/journal.ts`). What the
 * button says is a verb, and what it does is queue a command.
 */
function renderShare(entry: JournalEntry, share: JournalShare): HTMLElement {
  const element = document.createElement("div");
  element.className = "hud__share-row";

  const mark = document.createElement("span");
  // Filled, against the entries' hollow: an offer is a thing to do and a record
  // on file is a thing that is done.
  mark.className = "objective-diamond hud__journal-mark";
  mark.setAttribute("aria-hidden", "true");

  const note = document.createElement("div");
  note.className = "hud__journal-note";
  note.textContent = entry.note;

  const provenance = document.createElement("div");
  provenance.className = "hud__journal-provenance";
  provenance.textContent = provenanceOf(entry);
  provenance.append(cite(entry.event));

  element.append(
    mark,
    note,
    provenance,
    button("tell them", () => {
      share.onTell(entry);
    }),
  );

  return element;
}
